import React from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  Box,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Stack,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  NavigateBefore,
  NavigateNext,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { ReadMoreText } from "../../views/marketingCampaign/components/ReadMoreText";
import { StandardTableActions } from "../StandardTableActions/StandardTableActions";
import { StatusRenderer } from "../ActiveInActiveStatus/ActiveInActiveStatus";
import { useNavigate } from "react-router-dom";
import {
  deleteWhatsAppTemplate,
  assignTemplateFor,
} from "../../redux/whatsappTemplate/whatsappTemplateAction";
import { useDispatchWrapper } from "../../hooks";
import { IWhatsAppTemplateData } from "../../redux/whatsappTemplate/whatsappTemplate.types";
import { IQuickReplyTemplateData } from "../../redux/quickReplyTemplates/quickReplyTemplate.types";
import { deleteQuickReplyTemplate } from "../../redux/quickReplyTemplates/quickReplyTemplateAction";
import { formatText } from "../../helpers";
import { TemplateForPopover } from "./components/TemplateForPopover";
import { ViewWhatsappTemplateDetails } from "../../views/WhatsAppTemplate/components/ViewWhatsappTemplateDetails";

interface WhatsAppTemplateTableProps {
  fetchList?: () => void;
  templates: IWhatsAppTemplateData[];
  selectedTemplate: string | null;
  onTemplateSelect: (
    name: string,
    company_outlet_uuid: string,
    template: IWhatsAppTemplateData,
  ) => (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  // Old cursor-based pagination props (kept for backward compatibility)
  paging?: {
    cursors: { before: string; after: string };
    next: string;
    itemPerPage: number;
  };
  // New traditional pagination props
  currentPage?: number;
  totalRecords?: number;
  onPageChange: (
    direction?: "next" | "prev",
    cursor?: string,
    rowsPerPage?: number,
  ) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPage: number;
  searchTemplateName?: string;
  onTemplatePreview: (template: IWhatsAppTemplateData) => void;
  showSelectOption?: boolean;
  isFromQuickReply?: boolean;
  isFromChatModule?: {
    toNumber?: string;
    isFromStartConversation?: boolean;
    isFromQuickReply?: boolean;
    onClose?: () => void;
  };
}

export const WhatsAppTemplateTable: React.FC<WhatsAppTemplateTableProps> = ({
  fetchList,
  templates,
  selectedTemplate,
  onTemplateSelect,
  paging,
  currentPage = 1,
  totalRecords = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPage,
  searchTemplateName,
  onTemplatePreview,
  showSelectOption = true,
  isFromQuickReply = false,
  isFromChatModule,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openView, setOpenView] = React.useState<IWhatsAppTemplateData | null>(
    null,
  );

  // State for Template For popover
  const [templateForPopover, setTemplateForPopover] = React.useState<{
    anchorEl: HTMLElement | null;
    template: IWhatsAppTemplateData | null;
    selectedOptions: string[];
  }>({
    anchorEl: null,
    template: null,
    selectedOptions: [],
  });

  const handleTemplateForChipClick = (
    event: React.MouseEvent<HTMLElement>,
    template: IWhatsAppTemplateData,
  ) => {
    event.stopPropagation();
    const currentTemplateFor = Array.isArray(template.template_for)
      ? template.template_for.filter((t) => t !== "" && t !== "-1")
      : [];
    setTemplateForPopover({
      anchorEl: event.currentTarget,
      template,
      selectedOptions: currentTemplateFor as string[],
    });
  };

  const handleTemplateForPopoverClose = () => {
    setTemplateForPopover({
      anchorEl: null,
      template: null,
      selectedOptions: [],
    });
  };

  const handleOptionToggle = (option: string) => {
    setTemplateForPopover((prev) => ({
      ...prev,
      selectedOptions: prev.selectedOptions.includes(option)
        ? prev.selectedOptions.filter((o) => o !== option)
        : [...prev.selectedOptions, option],
    }));
  };

  const handleSaveTemplateFor = () => {
    if (templateForPopover.template) {
      dispatch(
        assignTemplateFor(
          templateForPopover.template.name,
          templateForPopover.template.company_outlet_uuid,
          templateForPopover.selectedOptions,
          (isSuccess) => {
            if (isSuccess) {
              fetchList?.();
              handleTemplateForPopoverClose();
            }
          },
        ),
      );
    }
  };

  // Render Template For Chip
  const renderTemplateForChip = (template: IWhatsAppTemplateData) => {
    const templateForValues = Array.isArray(template.template_for)
      ? template.template_for.filter((t) => t !== "" && t !== "-1")
      : [];

    const displayValue =
      templateForValues.length > 0
        ? templateForValues.map((t) => formatText(t)).join(", ")
        : "Not Assigned";

    return (
      <Chip
        label={displayValue}
        size="small"
        deleteIcon={<KeyboardArrowDown />}
        onDelete={(e) => handleTemplateForChipClick(e as any, template)}
        onClick={(e) => handleTemplateForChipClick(e, template)}
        sx={{
          cursor: "pointer",
          borderRadius: "6px",
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "capitalize",
          backgroundColor:
            templateForValues.length > 0 ? "primary.light" : "grey.100",
          color:
            templateForValues.length > 0 ? "primary.main" : "text.secondary",
          border: "1px solid",
          borderColor:
            templateForValues.length > 0 ? "primary.main" : "grey.300",
          "&:hover": {
            backgroundColor:
              templateForValues.length > 0 ? "primary.light" : "grey.200",
          },
          "& .MuiChip-deleteIcon": {
            color:
              templateForValues.length > 0 ? "primary.main" : "text.secondary",
            fontSize: "1.2rem",
          },
        }}
      />
    );
  };

  // Type helper for filtering - both types have the same structure for filtering purposes
  type TemplateForFilter = IWhatsAppTemplateData;

  const filteredTemplates = (templates as TemplateForFilter[]).filter(
    (template: TemplateForFilter) => {
      if (!searchTemplateName) return true;
      const searchTerms = searchTemplateName.toLowerCase().trim().split(/\s+/);
      const templateName = template.name.toLowerCase().trim();
      return searchTerms.every((term) => templateName.includes(term));
    },
  ) as IWhatsAppTemplateData[];

  // New traditional pagination logic
  const isBackDisabled = currentPage <= 1;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const isNextDisabled =
    currentPage >= totalPages || filteredTemplates.length < rowsPerPage;

  const handleDeleteTemplate = (template: IWhatsAppTemplateData) => {
    if (isFromQuickReply) {
      dispatch(
        deleteQuickReplyTemplate(
          template.company_outlet_uuid,
          template.name,
          (isSuccess) => {
            if (isSuccess) {
              fetchList?.();
            }
          },
        ),
      );
    } else {
      dispatch(
        deleteWhatsAppTemplate(
          template.company_outlet_uuid,
          template.name,
          (isSuccess) => {
            if (isSuccess) {
              fetchList?.();
            }
          },
        ),
      );
    }
  };

  const handleEditTemplate = (
    template: IWhatsAppTemplateData | IQuickReplyTemplateData,
  ) => {
    navigate(
      `/meta-templates/manage/${(template as IWhatsAppTemplateData).meta_id}`,
    );
  };

  // Mobile Card View
  const renderMobileCard = (template: IWhatsAppTemplateData) => {
    return (
      <Card
        key={template.name}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid #ddd",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 3px",
          backgroundColor: "background.paper",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "rgba(0, 0, 0, 0.15) 0px 4px 8px",
            transform: "translateY(-2px)",
          },
        }}
        onClick={() => onTemplatePreview(template)}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header with Template Name and Status */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1, mr: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  mb: 0.5,
                  wordBreak: "break-word",
                }}
              >
                {formatText(template.name)}
              </Typography>
            </Box>
            {!isFromChatModule && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <StatusRenderer status={template.status || ""} />
              </Box>
            )}
          </Box>

          {/* Template For */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Template For
            </Typography>
            <Box onClick={(e) => e.stopPropagation()}>
              {renderTemplateForChip(template)}
            </Box>
          </Box>

          {/* Header */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Header
            </Typography>
            <Typography variant="body2">
              {template.header?.text || template.header?.format || "--"}
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Body
            </Typography>
            <ReadMoreText
              text={template.body?.text || "--"}
              maxLines={3}
              variant="body2"
            />
          </Box>

          {/* Footer */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Footer
            </Typography>
            <Typography variant="body2">
              {template.footer?.text || "--"}
            </Typography>
          </Box>

          {/* Buttons */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Buttons
            </Typography>
            <Typography variant="body2">
              {template.buttons?.buttons && template.buttons.buttons.length > 0
                ? template.buttons.buttons
                    .map((button: any) => button.text)
                    .join(", ")
                : "--"}
            </Typography>
          </Box>

          {/* Actions Row */}
          {!isFromChatModule && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                pt: 1.5,
                borderTop: "1px solid #e0e0e0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {showSelectOption && (
                <Radio
                  checked={template.name === selectedTemplate}
                  onChange={onTemplateSelect(
                    template.name,
                    template.company_outlet_uuid,
                    template,
                  )}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <StandardTableActions
                onViewClick={() => setOpenView(template)}
                onDeleteClick={() => handleDeleteTemplate(template)}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {/* Mobile Card View */}
      {isMobile ? (
        <Box sx={{ mt: 2 }}>
          {filteredTemplates?.map((template) => renderMobileCard(template))}

          {/* Pagination */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={isMobile && isFromChatModule ? 1 : 2}
            sx={{
              ...(isMobile && isFromChatModule
                ? {
                    flexWrap: "wrap",
                    gap: 1,
                  }
                : {}),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize:
                    isMobile && isFromChatModule ? "0.75rem" : undefined,
                }}
              >
                Rows per page:
              </Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newRowsPerPage = e.target.value as number;
                    onRowsPerPageChange(newRowsPerPage);
                    onPageChange(undefined, undefined, newRowsPerPage);
                  }}
                  sx={{
                    color: "text.secondary",
                    fontSize:
                      isMobile && isFromChatModule ? "0.75rem" : undefined,
                  }}
                >
                  <MenuItem value={9}>9</MenuItem>
                  <MenuItem value={18}>18</MenuItem>
                  <MenuItem value={36}>36</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack
              direction="row"
              spacing={isMobile && isFromChatModule ? 0.5 : 1}
              alignItems="center"
            >
              <IconButton
                onClick={() => onPageChange("prev")}
                disabled={isBackDisabled}
                size={isMobile && isFromChatModule ? "small" : "medium"}
                sx={{
                  ...(isMobile && isFromChatModule
                    ? {
                        padding: "4px",
                      }
                    : {}),
                }}
              >
                <NavigateBefore />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize:
                      isMobile && isFromChatModule ? "0.75rem" : undefined,
                  }}
                >
                  Back
                </Typography>
              </IconButton>

              <IconButton
                onClick={() => onPageChange("next")}
                disabled={isNextDisabled}
                size={isMobile && isFromChatModule ? "small" : "medium"}
                sx={{
                  ...(isMobile && isFromChatModule
                    ? {
                        padding: "4px",
                      }
                    : {}),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize:
                      isMobile && isFromChatModule ? "0.75rem" : undefined,
                  }}
                >
                  Next
                </Typography>
                <NavigateNext />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer sx={{ mt: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                {showSelectOption && (
                  <TableCell
                    sx={{
                      color: "#fff",
                      fontSize: "0.95rem",
                      fontWeight: 500,
                    }}
                  >
                    Select
                  </TableCell>
                )}

                {!isFromChatModule && (
                  <>
                    <TableCell
                      sx={{
                        color: "#fff",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        minWidth: "100px",
                      }}
                    >
                      Actions
                    </TableCell>
                  </>
                )}

                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    minWidth: "200px",
                  }}
                >
                  Template Name
                </TableCell>

                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    minWidth: "200px",
                  }}
                >
                  Template For
                </TableCell>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    minWidth: "100px",
                  }}
                >
                  Header
                </TableCell>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    minWidth: "200px",
                  }}
                >
                  Body
                </TableCell>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    minWidth: "200px",
                  }}
                >
                  Footer
                </TableCell>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    minWidth: "200px",
                  }}
                >
                  Buttons
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates?.map(
                (template: IWhatsAppTemplateData, index: any) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => onTemplatePreview(template)}
                    sx={{ cursor: "pointer" }}
                  >
                    {showSelectOption && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Radio
                          checked={template.name === selectedTemplate}
                          onChange={onTemplateSelect(
                            template.name,
                            template.company_outlet_uuid,
                            template,
                          )}
                        />
                      </TableCell>
                    )}

                    {!isFromChatModule && (
                      <>
                        {" "}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <StatusRenderer status={template.status || ""} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <StatusRenderer status={template.category || ""} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <StandardTableActions
                            onViewClick={() =>
                              // setOpenView(convertToMetaTemplate(template))
                              setOpenView(template)
                            }
                            {...(template.status === "REJECTED" && {
                              onEditClick: () => handleEditTemplate(template),
                            })}
                            onDeleteClick={() => handleDeleteTemplate(template)}
                          />
                        </TableCell>
                      </>
                    )}

                    <TableCell>{formatText(template.name)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {renderTemplateForChip(template)}
                    </TableCell>
                    <TableCell>
                      {template.header?.text || template.header?.format || "--"}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        return (
                          <ReadMoreText
                            text={template.body.text}
                            maxLines={2}
                            variant="body2"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>{template.footer?.text || "--"}</TableCell>
                    <TableCell>
                      {template.buttons?.buttons &&
                      template.buttons.buttons.length > 0
                        ? template.buttons.buttons
                            .map((button: any) => button.text)
                            .join(", ")
                        : "--"}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            p={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Rows per page:
              </Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newRowsPerPage = e.target.value as number;
                    onRowsPerPageChange(newRowsPerPage);
                    onPageChange(undefined, undefined, newRowsPerPage);
                  }}
                  sx={{ color: "text.secondary" }}
                >
                  <MenuItem value={9}>9</MenuItem>
                  <MenuItem value={18}>18</MenuItem>
                  <MenuItem value={36}>36</MenuItem>
                  <MenuItem value={72}>72</MenuItem>
                  <MenuItem value={144}>144</MenuItem>
                </Select>
              </FormControl>

              <IconButton
                onClick={() => onPageChange("prev")}
                disabled={isBackDisabled}
              >
                <NavigateBefore />
                <Typography variant="body2">Back</Typography>
              </IconButton>

              <IconButton
                onClick={() => onPageChange("next")}
                disabled={isNextDisabled}
              >
                <Typography variant="body2">Next</Typography>
                <NavigateNext />
              </IconButton>
            </Stack>
          </Box>
        </TableContainer>
      )}
      {openView && (
        <ViewWhatsappTemplateDetails
          type={"ANALYTICS"}
          open={true}
          // metaTemplate={openView}
          onClose={() => setOpenView(null)}
          whatsappTemplateData={openView}
        />
      )}

      {/* Template For Popover */}
      <TemplateForPopover
        anchorEl={templateForPopover.anchorEl}
        selectedOptions={templateForPopover.selectedOptions}
        onClose={handleTemplateForPopoverClose}
        onOptionToggle={handleOptionToggle}
        onSave={handleSaveTemplateFor}
      />
    </>
  );
};
